import groupBy from 'lodash.groupby';
import kebabCase from 'lodash.kebabcase';
import keyBy from 'lodash.keyby';
import toLower from 'lodash.tolower';
import compact from 'lodash.compact';

import { CURRENCY_USD, PLAN_GUEST, PLAN_STANDARD, PLAN_PROFESSIONAL } from './app';

import { parsePreset } from '../utils/preset';

const bitcoinCoreOnchainPreset = parsePreset(
  [
    {
      code: 'addresses.ActiveCount',
      asset: 'BTC',
    },
    {
      code: 'addresses.NewNonZeroCount',
      asset: 'BTC',
    },
    {
      code: 'transactions.Count',
      asset: 'BTC',
    },
    {
      code: 'transactions.TransfersVolumeSum',
      asset: 'BTC',
    },
    {
      code: 'fees.VolumeSum',
      asset: 'BTC',
    },
    {
      code: 'mining.HashRateMean',
      asset: 'BTC',
    },
    {
      code: 'mining.DifficultyLatest',
      asset: 'BTC',
    },
    {
      code: 'blockchain.BlockCount',
      asset: 'BTC',
    },
    {
      code: 'blockchain.BlockIntervalMean',
      asset: 'BTC',
    },
  ],
  {
    xl: '4x4x4|6x6|6x6|6x6',
    lg: '6x6|6x6|6x6|6x6|12',
    md: '12|12|12|12|12|12|12|12|12',
  },
);

const bitcoinCoreOnchain = {
  asset: 'BTC',
  name: 'Core On-Chain',
  description: 'Collection of core on-chain metrics showing the current state and health of the Bitcoin network.',
  plan: PLAN_GUEST,
  items: bitcoinCoreOnchainPreset('items'),
  layouts: bitcoinCoreOnchainPreset('layouts'),
};

const bitcoinFuturesPreset = parsePreset(
  [
    {
      code: 'derivatives.FuturesVolumeDailyLatest',
      asset: 'BTC',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesOpenInterestLatest',
      asset: 'BTC',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesVolumeDailyAllSum',
      asset: 'BTC',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesOpenInterestAllSum',
      asset: 'BTC',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesVolumeDailyPerpetualSum',
      asset: 'BTC',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesOpenInterestPerpetualSum',
      asset: 'BTC',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesFundingRatePerpetual',
      asset: 'BTC',
    },
  ],
  { xl: '6x6|6x6|4x4x4', md: '12|12|12|12|12|12|12' },
);

const bitcoinFutures = {
  asset: 'BTC',
  name: 'Futures',
  description:
    'Collection of Bitcoin futures contracts metrics showing daily volume, open interest, and perpetual funding rates in major exchanges. Charts show aggregate numbers over all supported exchanges. View full charts to select individual exchanges.',
  plan: PLAN_GUEST,
  items: bitcoinFuturesPreset('items'),
  layouts: bitcoinFuturesPreset('layouts'),
};

const bitcoinAddressesPreset = parsePreset(
  [
    {
      code: 'addresses.Count',
      asset: 'BTC',
    },
    {
      code: 'addresses.NewNonZeroCount',
      asset: 'BTC',
    },
    {
      code: 'addresses.ActiveCount',
      asset: 'BTC',
    },
    {
      code: 'addresses.SendingCount',
      asset: 'BTC',
    },
    {
      code: 'addresses.ReceivingCount',
      asset: 'BTC',
    },
  ],
  {
    xl: '4x4x4|6x6',
    lg: '6x6|6x6|12',
    md: '12|12|12|12|12',
  },
);

const bitcoinAddresses = {
  asset: 'BTC',
  name: 'Addresses',
  description: 'Collection of core address metrics for the Bitcoin network.',
  plan: PLAN_GUEST,
  items: bitcoinAddressesPreset('items'),
  layouts: bitcoinAddressesPreset('layouts'),
};

const bitcoinBalancesPreset = parsePreset(
  [
    {
      code: 'addresses.NonZeroCount',
      asset: 'BTC',
    },
    {
      code: 'addresses.MinPointZero1Count',
      asset: 'BTC',
    },
    {
      code: 'addresses.MinPoint1Count',
      asset: 'BTC',
    },
    {
      code: 'addresses.Min1Count',
      asset: 'BTC',
    },
    {
      code: 'addresses.Min10Count',
      asset: 'BTC',
    },
    {
      code: 'addresses.Min100Count',
      asset: 'BTC',
    },
    {
      code: 'addresses.Min1KCount',
      asset: 'BTC',
    },
    {
      code: 'addresses.Min10KCount',
      asset: 'BTC',
    },
  ],
  {
    xl: '6x6|6x6|6x6|6x6',
    lg: '6x6|6x6|6x6|6x6',
    md: '12|12|12|12|12|12|12|12',
  },
);

const bitcoinBalances = {
  asset: 'BTC',
  name: 'Balances',
  description:
    'Collection of metrics showing the evolution of address counts with specific balances in the Bitcoin network.',
  plan: PLAN_STANDARD,
  items: bitcoinBalancesPreset('items'),
  layouts: bitcoinBalancesPreset('layouts'),
};

const bitcoinMarketTopsBottomsPreset = parsePreset(
  [
    {
      code: 'indicators.NetUnrealizedProfitLoss',
      asset: 'BTC',
    },
    {
      code: 'supply.ProfitRelative',
      asset: 'BTC',
    },
    {
      code: 'blockchain.UtxoProfitRelative',
      asset: 'BTC',
    },
    {
      code: 'market.MvrvZScore',
      asset: 'BTC',
    },
    {
      code: 'indicators.PuellMultiple',
      asset: 'BTC',
    },
    {
      code: 'indicators.ReserveRisk',
      asset: 'BTC',
    },
  ],
  {
    xl: '6x6|6x6|6x6',
    md: '12|12|12|12|12|12',
  },
);

const bitcoinMarketTopsBottoms = {
  asset: 'BTC',
  name: 'Market Tops/Bottoms',
  description: 'Advanced indicators for investors assessing global tops and bottoms of the Bitcoin market cycles.',
  plan: PLAN_STANDARD,
  items: bitcoinMarketTopsBottomsPreset('items'),
  layouts: bitcoinMarketTopsBottomsPreset('layouts'),
};

const bitcoinExchangeActivityPreset = parsePreset(
  [
    {
      code: 'transactions.TransfersVolumeToExchangesSum',
      asset: 'BTC',
    },
    {
      code: 'transactions.TransfersVolumeFromExchangesSum',
      asset: 'BTC',
    },
    {
      code: 'transactions.TransfersVolumeExchangesNet',
      asset: 'BTC',
    },
    {
      code: 'distribution.BalanceExchanges',
      asset: 'BTC',
    },
    {
      code: 'transactions.TransfersToExchangesCount',
      asset: 'BTC',
    },
    {
      code: 'transactions.TransfersFromExchangesCount',
      asset: 'BTC',
    },
  ],
  {
    xl: '4x4x4|4x4x4',
    lg: '6x6|6x6|6x6',
    md: '12|12|12|12|12|12',
  },
);

const bitcoinExchangeActivity = {
  asset: 'BTC',
  name: 'Exchange Activity',
  description:
    'Collection of metrics of on–chain activity related to centralized exchanges such as in and out flow of exchange wallets.',
  plan: PLAN_STANDARD,
  items: bitcoinExchangeActivityPreset('items'),
  layouts: bitcoinExchangeActivityPreset('layouts'),
};

const bitcoinWarningIndicatorsPreset = parsePreset(
  [
    {
      code: 'indicators.SoprAdjusted',
      asset: 'BTC',
    },
    {
      code: 'indicators.Cdd',
      asset: 'BTC',
    },
    {
      code: 'indicators.Asol',
      asset: 'BTC',
    },
    {
      code: 'indicators.Msol',
      asset: 'BTC',
    },
  ],
  {
    xl: '6x6|6x6',
    md: '12|12|12|12',
  },
);

const bitcoinWarningIndicators = {
  asset: 'BTC',
  name: 'Warning Indicators',
  description:
    'Warning indicators are related to on–chain activity where a sudden deviation from typical values is an indication of unusual behaviour to be watched and examined closely.',
  plan: PLAN_STANDARD,
  items: bitcoinWarningIndicatorsPreset('items'),
  layouts: bitcoinWarningIndicatorsPreset('layouts'),
};

const bitcoinMinersPreset = parsePreset(
  [
    {
      code: 'mining.HashRateMean',
      asset: 'BTC',
    },
    {
      code: 'mining.DifficultyLatest',
      asset: 'BTC',
    },
    {
      code: 'mining.RevenueSum',
      asset: 'BTC',
    },
    {
      code: 'mining.RevenueFromFees',
      asset: 'BTC',
    },
    {
      code: 'blockchain.BlockCount',
      asset: 'BTC',
    },
    {
      code: 'blockchain.BlockIntervalMean',
      asset: 'BTC',
    },
  ],
  {
    xl: '6x6|6x6|6x6',
    md: '12|12|12|12|12|12',
  },
);

const bitcoinMiners = {
  asset: 'BTC',
  name: 'Miners',
  description: 'Overview of essential miner metrics related to hashing power, revenue, and block production.',
  plan: PLAN_STANDARD,
  items: bitcoinMinersPreset('items'),
  layouts: bitcoinMinersPreset('layouts'),
};

const bitcoinHodlersPreset = parsePreset(
  [
    {
      code: 'indicators.SoprMore155',
      asset: 'BTC',
    },
    {
      code: 'market.MvrvMore155',
      asset: 'BTC',
    },
    {
      code: 'indicators.Msol',
      asset: 'BTC',
    },
    {
      code: 'indicators.Cdd',
      asset: 'BTC',
    },
  ],
  {
    xl: '6x6|6x6',
    md: '12|12|12|12',
  },
);

const bitcoinHodlers = {
  asset: 'BTC',
  name: 'Hodlers',
  description:
    'Hodler metrics relate to on–chain activity of long-term investors and can be used to assess the behaviour of Hodlers.',
  plan: PLAN_PROFESSIONAL,
  items: bitcoinHodlersPreset('items'),
  layouts: bitcoinHodlersPreset('layouts'),
};

const ethereumCoreOnchainPreset = parsePreset(
  [
    {
      code: 'addresses.ActiveCount',
      asset: 'ETH',
    },
    {
      code: 'addresses.NewNonZeroCount',
      asset: 'ETH',
    },
    {
      code: 'transactions.Count',
      asset: 'ETH',
    },
    {
      code: 'transactions.TransfersVolumeSum',
      asset: 'ETH',
    },
    {
      code: 'fees.VolumeSum',
      asset: 'ETH',
    },
    {
      code: 'fees.GasUsedSum',
      asset: 'ETH',
    },
    {
      code: 'mining.HashRateMean',
      asset: 'ETH',
    },
    {
      code: 'mining.DifficultyLatest',
      asset: 'ETH',
    },
    {
      code: 'blockchain.BlockCount',
      asset: 'ETH',
    },
    {
      code: 'blockchain.BlockIntervalMean',
      asset: 'ETH',
    },
  ],
  {
    xl: '4x4x4|6x6|6x6|6x6|12',
    lg: '6x6|6x6|6x6|6x6|12|12',
    md: '12|12|12|12|12|12|12|12|12|12',
  },
);

const ethereumCoreOnchain = {
  asset: 'ETH',
  name: 'Core On-Chain',
  description: 'Collection of core on-chain metrics showing the current state and health of the Ethereum network.',
  plan: PLAN_GUEST,
  items: ethereumCoreOnchainPreset('items'),
  layouts: ethereumCoreOnchainPreset('layouts'),
};

const ethereumFuturesPreset = parsePreset(
  [
    {
      code: 'derivatives.FuturesVolumeDailyLatest',
      asset: 'ETH',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesOpenInterestLatest',
      asset: 'ETH',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesVolumeDailyAllSum',
      asset: 'ETH',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesOpenInterestAllSum',
      asset: 'ETH',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesVolumeDailyPerpetualSum',
      asset: 'ETH',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesOpenInterestPerpetualSum',
      asset: 'ETH',
      currency: CURRENCY_USD,
    },
    {
      code: 'derivatives.FuturesFundingRatePerpetual',
      asset: 'ETH',
    },
  ],
  { xl: '6x6|6x6|4x4x4', md: '12|12|12|12|12|12|12' },
);

const ethereumFutures = {
  asset: 'ETH',
  name: 'Futures',
  description:
    'Collection of Ethereum futures contracts metrics showing daily aggregate volume, open interest, and perpetual funding rates in major exchanges. View full charts to see supported exchanges.',
  plan: PLAN_GUEST,
  items: ethereumFuturesPreset('items'),
  layouts: ethereumFuturesPreset('layouts'),
};

const ethereumAddressesPreset = parsePreset(
  [
    {
      code: 'addresses.Count',
      asset: 'ETH',
    },
    {
      code: 'addresses.NewNonZeroCount',
      asset: 'ETH',
    },
    {
      code: 'addresses.ActiveCount',
      asset: 'ETH',
    },
    {
      code: 'addresses.SendingCount',
      asset: 'ETH',
    },
    {
      code: 'addresses.ReceivingCount',
      asset: 'ETH',
    },
  ],
  {
    xl: '4x4x4|6x6',
    lg: '6x6|6x6|12',
    md: '12|12|12|12|12',
  },
);

const ethereumAddresses = {
  asset: 'ETH',
  name: 'Addresses',
  description: 'Collection of core address metrics for the Ethereum network.',
  plan: PLAN_GUEST,
  items: ethereumAddressesPreset('items'),
  layouts: ethereumAddressesPreset('layouts'),
};

const ethereumBalancesPreset = parsePreset(
  [
    {
      code: 'addresses.NonZeroCount',
      asset: 'ETH',
    },
    {
      code: 'addresses.MinPointZero1Count',
      asset: 'ETH',
    },
    {
      code: 'addresses.MinPoint1Count',
      asset: 'ETH',
    },
    {
      code: 'addresses.Min1Count',
      asset: 'ETH',
    },
    {
      code: 'addresses.Min10Count',
      asset: 'ETH',
    },
    {
      code: 'addresses.Min100Count',
      asset: 'ETH',
    },
    {
      code: 'addresses.Min1KCount',
      asset: 'ETH',
    },
    {
      code: 'addresses.Min10KCount',
      asset: 'ETH',
    },
  ],
  {
    xl: '6x6|6x6|6x6|6x6',
    lg: '6x6|6x6|6x6|6x6',
    md: '12|12|12|12|12|12|12|12',
  },
);

const ethereumBalances = {
  asset: 'ETH',
  name: 'Balances',
  description:
    'Collection of metrics showing the evolution of address counts with specific balances in the Ethereum network.',
  plan: PLAN_STANDARD,
  items: ethereumBalancesPreset('items'),
  layouts: ethereumBalancesPreset('layouts'),
};

const ethereumGasPreset = parsePreset(
  [
    {
      code: 'fees.VolumeSum',
      asset: 'ETH',
    },
    {
      code: 'fees.GasUsedSum',
      asset: 'ETH',
    },
    {
      code: 'fees.GasUsedMean',
      asset: 'ETH',
    },
    {
      code: 'fees.GasUsedMedian',
      asset: 'ETH',
    },
    {
      code: 'fees.GasLimitTxMean',
      asset: 'ETH',
    },
  ],
  {
    xl: '4x4x4|6x6',
    lg: '6x6|6x6|12',
    md: '12|12|12|12|12',
  },
);

const ethereumGas = {
  asset: 'ETH',
  name: 'Gas',
  description: 'Collection of core gas and fee metrics iforn the Ethereum network.',
  plan: PLAN_STANDARD,
  items: ethereumGasPreset('items'),
  layouts: ethereumGasPreset('layouts'),
};

const ethereumExchangeActivityPreset = parsePreset(
  [
    {
      code: 'transactions.TransfersVolumeToExchangesSum',
      asset: 'ETH',
    },
    {
      code: 'transactions.TransfersVolumeFromExchangesSum',
      asset: 'ETH',
    },
    {
      code: 'transactions.TransfersVolumeExchangesNet',
      asset: 'ETH',
    },
    {
      code: 'distribution.BalanceExchanges',
      asset: 'ETH',
    },
    {
      code: 'transactions.TransfersToExchangesCount',
      asset: 'ETH',
    },
    {
      code: 'transactions.TransfersFromExchangesCount',
      asset: 'ETH',
    },
  ],
  {
    xl: '4x4x4|4x4x4',
    lg: '6x6|6x6|6x6',
    md: '12|12|12|12|12|12',
  },
);

const ethereumExchangeActivity = {
  asset: 'ETH',
  name: 'Exchange Activity',
  description:
    'Collection of metrics of on–chain activity related to centralized exchanges such as in and out flow of exchange wallets.',
  plan: PLAN_STANDARD,
  items: ethereumExchangeActivityPreset('items'),
  layouts: ethereumExchangeActivityPreset('layouts'),
};

const ethereumMinersPreset = parsePreset(
  [
    {
      code: 'mining.HashRateMean',
      asset: 'ETH',
    },
    {
      code: 'mining.DifficultyLatest',
      asset: 'ETH',
    },
    {
      code: 'mining.RevenueSum',
      asset: 'ETH',
    },
    {
      code: 'mining.RevenueFromFees',
      asset: 'ETH',
    },
    {
      code: 'blockchain.BlockCount',
      asset: 'ETH',
    },
    {
      code: 'blockchain.BlockIntervalMean',
      asset: 'ETH',
    },
  ],
  {
    xl: '6x6|6x6|6x6',
    md: '12|12|12|12|12|12',
  },
);

const ethereumMiners = {
  asset: 'ETH',
  name: 'Miners',
  description: 'Overview of essential miner metrics related to hashing power, revenue, and block production.',
  plan: PLAN_STANDARD,
  items: ethereumMinersPreset('items'),
  layouts: ethereumMinersPreset('layouts'),
};

const PRESETS = compact([
  bitcoinCoreOnchain,
  bitcoinFutures,
  bitcoinAddresses,
  bitcoinBalances,
  bitcoinMarketTopsBottoms,
  bitcoinExchangeActivity,
  bitcoinWarningIndicators,
  bitcoinMiners,
  bitcoinHodlers,
  ethereumCoreOnchain,
  ethereumFutures,
  ethereumAddresses,
  ethereumBalances,
  ethereumGas,
  ethereumExchangeActivity,
  ethereumMiners,
]).map((item) => ({ ...item, slug: `${toLower(item.asset)}-${kebabCase(item.name)}` }));

export const DASHBOARDS_BY_ASSET = groupBy(PRESETS, 'asset');
export const DASHBOARDS_BY_SLUG = keyBy(PRESETS, 'slug');
export const DASHBOARD_SLUGS = PRESETS.map((item) => item.slug);
